import {
  FormApi as _FormApi,
  FormAsyncValidateOrFn,
  FormValidateOrFn,
  useForm,
} from "@tanstack/react-form";
import classNames from "classnames";
import { ReactNode } from "react";
import Button from "./Button";
import styles from "./Form.module.css";

export type UseFormReturn<TFormData> = ReturnType<
  typeof useForm<
    TFormData,
    FormValidateOrFn<TFormData> | undefined,
    FormValidateOrFn<TFormData> | undefined,
    FormAsyncValidateOrFn<TFormData> | undefined,
    FormValidateOrFn<TFormData> | undefined,
    FormAsyncValidateOrFn<TFormData> | undefined,
    FormValidateOrFn<TFormData> | undefined,
    FormAsyncValidateOrFn<TFormData> | undefined,
    FormValidateOrFn<TFormData> | undefined,
    FormAsyncValidateOrFn<TFormData> | undefined,
    FormAsyncValidateOrFn<TFormData> | undefined,
    unknown
  >
>;
export type FormApi<TFormData> = _FormApi<
  TFormData,
  FormValidateOrFn<TFormData> | undefined,
  FormValidateOrFn<TFormData> | undefined,
  FormAsyncValidateOrFn<TFormData> | undefined,
  FormValidateOrFn<TFormData> | undefined,
  FormAsyncValidateOrFn<TFormData> | undefined,
  FormValidateOrFn<TFormData> | undefined,
  FormAsyncValidateOrFn<TFormData> | undefined,
  FormValidateOrFn<TFormData> | undefined,
  FormAsyncValidateOrFn<TFormData> | undefined,
  FormAsyncValidateOrFn<TFormData> | undefined,
  unknown
>;

interface Props<TFormData> {
  children?: ReactNode | ((formApi: UseFormReturn<TFormData>) => ReactNode);
  className?: string;
  defaultValues?: TFormData;
  onSubmit?: (props: {
    value: TFormData;
    formApi: FormApi<TFormData>;
  }) => any | Promise<any>;
  submitText?: string;
  scrollable?: boolean;
}

const Form = <TFormData extends object>({
  children,
  className,
  defaultValues,
  onSubmit,
  submitText,
  scrollable,
}: Props<TFormData>) => {
  const form = useForm({
    defaultValues,
    onSubmit,
  });

  return (
    <form
      className={classNames(className, styles.form, {
        [styles.scrollable]: scrollable,
      })}
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
    >
      <form.Subscribe selector={(state) => [state.errors]}>
        {([errors]) => {
          if (errors?.length === 0) {
            return;
          }
          return <p className={styles.errors}>{errors}</p>;
        }}
      </form.Subscribe>
      {typeof children === "function" ? children(form) : children}
      {submitText && (
        <form.Subscribe selector={(state) => [state.isSubmitting]}>
          {([isSubmitting]) => (
            <Button
              type="primary"
              buttonProps={{
                type: "submit",
                disabled: isSubmitting, // !canSubmit || isSubmitting,
              }}
            >
              {isSubmitting ? "..." : submitText}
            </Button>
          )}
        </form.Subscribe>
      )}
    </form>
  );
};

export default Form;
